import * as React from 'react';
import { httpsCallable } from 'firebase/functions';
import { Bot, Check, Loader2, Play, Sparkles } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Textarea } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Skeleton } from '../../../../components/ui/skeleton';
import { useToast } from '../../../../components/ui/toast';
import { useTargetUidPayload } from '../../../../hooks/useTargetUidPayload';
import { useAgentPresets } from '../../../../hooks/useAgentPresets';
import { functions } from '../../../../lib/firebase';
import { cn } from '../../../../lib/utils';
import type { FormState } from '../state';

interface Props {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}

export function AgentTab({ form, setForm }: Props) {
  const presets = useAgentPresets();
  const { toast } = useToast();
  const adminPayload = useTargetUidPayload();
  const [introBusy, setIntroBusy] = React.useState(false);
  const [outroBusy, setOutroBusy] = React.useState(false);

  const visiblePresets = (presets.data ?? []).filter((p) => !p.archived);
  const selectedId = form.selectedAgentPresetId ?? null;

  function selectPreset(id: string) {
    setForm((p) => ({ ...p, selectedAgentPresetId: id }));
  }

  async function regenerateIntro() {
    setIntroBusy(true);
    try {
      const fn = httpsCallable<
        { phrase?: string; voiceId?: string; model?: string; uid?: string },
        { ok: true; url: string }
      >(functions, 'generateIntroAudio');
      const r = await fn({
        phrase: form.introPhrase,
        voiceId: form.ttsVoiceId,
        model: form.ttsModel,
        ...adminPayload,
      });
      setForm((p) => ({ ...p, introAudioUrl: r.data.url }));
      toast({
        title: 'Begrüßung gerendert',
        description: 'Wird beim nächsten Anruf abgespielt.',
      });
    } catch (err) {
      toast({
        title: 'Generierung fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setIntroBusy(false);
    }
  }

  function previewIntro() {
    if (!form.introAudioUrl) return;
    const audio = new Audio(`${form.introAudioUrl}?t=${Date.now()}`);
    void audio.play().catch(() => undefined);
  }

  async function regenerateOutro() {
    setOutroBusy(true);
    try {
      const fn = httpsCallable<
        { phrase?: string; voiceId?: string; model?: string; uid?: string },
        { ok: true; url: string }
      >(functions, 'generateOutroAudio');
      const r = await fn({
        phrase: form.outroPhrase ?? undefined,
        voiceId: form.ttsVoiceId,
        model: form.ttsModel,
        ...adminPayload,
      });
      setForm((p) => ({ ...p, outroAudioUrl: r.data.url }));
      toast({
        title: 'Verabschiedung gerendert',
        description: 'Wird beim nächsten Auflegen abgespielt.',
      });
    } catch (err) {
      toast({
        title: 'Generierung fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    } finally {
      setOutroBusy(false);
    }
  }

  function previewOutro() {
    if (!form.outroAudioUrl) return;
    const audio = new Audio(`${form.outroAudioUrl}?t=${Date.now()}`);
    void audio.play().catch(() => undefined);
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            Sprach-Agent wählen
          </CardTitle>
          <CardDescription>
            Wähle einen vordefinierten Agenten aus. Modell, Stimme, Sprache und
            Verhalten kommen aus dieser Vorlage — du musst nichts weiter
            einstellen. Begrüßung, Verabschiedung und Wissensbasis änderst du
            unten selbst.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {presets.isLoading ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : visiblePresets.length === 0 ? (
            <EmptyPresetState />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {visiblePresets.map((p) => {
                const active = selectedId === p.id;
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => selectPreset(p.id)}
                    className={cn(
                      'group flex flex-col gap-2 rounded-lg border p-4 text-left transition-all',
                      active
                        ? 'border-primary bg-primary/5 shadow-sm ring-2 ring-primary/30'
                        : 'border-border bg-card hover:border-primary/50 hover:bg-secondary/40',
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{p.icon || '🤖'}</span>
                        <span className="font-medium">{p.name}</span>
                      </div>
                      {active && (
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          <Check className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </div>
                    {p.description && (
                      <p className="text-xs text-muted-foreground">
                        {p.description}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Begrüßung beim Anrufstart</CardTitle>
          <CardDescription>
            Erste Phrase, die der Anrufer hört — wird in der Stimme deines
            Agents vorab gerendert. Beim Speichern automatisch neu generiert,
            wenn sich der Text geändert hat.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            rows={3}
            value={form.introPhrase ?? ''}
            placeholder="Hallo, mein Name ist Lisa. Wie kann ich Ihnen heute helfen?"
            onChange={(e) => setForm((p) => ({ ...p, introPhrase: e.target.value }))}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void regenerateIntro()}
              disabled={introBusy || !form.introPhrase?.trim()}
            >
              {introBusy ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              )}
              Neu generieren
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={previewIntro}
              disabled={!form.introAudioUrl}
            >
              <Play className="mr-1.5 h-3.5 w-3.5" />
              Vorhören
            </Button>
            <span className="text-xs text-muted-foreground">
              {form.introAudioUrl ? 'Audio gerendert' : 'Noch nicht generiert'}
              {' · '}
              {(form.introPhrase ?? '').length} / 500
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Verabschiedung beim Auflegen</CardTitle>
          <CardDescription>
            Letzter Satz, den der Anrufer hört, bevor der Agent auflegt. Wird
            einmal in der Stimme deines Agents vorab gerendert und im Anruf
            direkt aus der Datei abgespielt — danach beendet die KI das
            Gespräch selbstständig. Beim Speichern automatisch neu generiert,
            wenn sich Text oder Stimme geändert haben.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            rows={3}
            value={form.outroPhrase ?? ''}
            placeholder="Vielen Dank für Ihren Anruf. Auf Wiederhören!"
            onChange={(e) =>
              setForm((p) => ({ ...p, outroPhrase: e.target.value }))
            }
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void regenerateOutro()}
              disabled={outroBusy || !form.outroPhrase?.trim()}
            >
              {outroBusy ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              )}
              Neu generieren
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={previewOutro}
              disabled={!form.outroAudioUrl}
            >
              <Play className="mr-1.5 h-3.5 w-3.5" />
              Vorhören
            </Button>
            <span className="text-xs text-muted-foreground">
              {form.outroAudioUrl ? 'Audio gerendert' : 'Noch nicht generiert'}
              {' · '}
              {(form.outroPhrase ?? '').length} / 500
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Wissensbasis</CardTitle>
          <CardDescription>
            Verbindliche Fakten, auf die dein Agent zugreifen soll: Adresse,
            Öffnungszeiten, Preise, Notfallnummer, FAQ. Wird vor jedem
            Gespräch automatisch in den Kontext gelegt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Textarea
            rows={12}
            value={form.knowledgeBase ?? ''}
            placeholder={
              'Praxis-Adresse: Beispielstraße 1, 1010 Wien\n' +
              'Öffnungszeiten: Mo–Fr 09:00–18:00\n' +
              'Notfallnummer: +43 1 23456789\n' +
              'Preise: Erstberatung 60 €, Folgetermin 40 €\n' +
              '\n' +
              'FAQ:\n' +
              'Q: Bieten Sie Hausbesuche an?\n' +
              'A: Ja, Mittwoch nachmittags nach Vereinbarung.'
            }
            onChange={(e) =>
              setForm((p) => ({ ...p, knowledgeBase: e.target.value }))
            }
          />
          <div className="text-xs text-muted-foreground">
            {(form.knowledgeBase ?? '').length} / 20.000 Zeichen
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EmptyPresetState() {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border p-8 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
        <Bot className="h-5 w-5 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium">Noch keine Agenten verfügbar</p>
      <p className="max-w-sm text-xs text-muted-foreground">
        Sobald dein Anbieter mindestens einen Agent freigeschaltet hat, kannst
        du ihn hier auswählen.
      </p>
    </div>
  );
}
